import { TextInput, Tooltip } from "@mantine/core";
import { t } from "i18next";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { setErrorsFromResponseData } from "../../../../components/BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { GENERATOR_FORM_DATA } from "../../../../components/ObjectModals/GeneratorModal/GeneratorModal.constants";
import { useToast } from "../../../../mantine/notifications/useToast";
import { isValidTrId } from "../../../../utils/validation/isValidTrId";
import type {
  ExpressOnboardingGenerator,
  GetErrorFn,
  SetErrorFn
} from "../../ExpressOnboarding.types";
import { useExpressOnboardingMutations } from "../hooks/useExpressOnboardingMutations";

function getExpressOnboardingTableTrIdColumn(
  getError: GetErrorFn,
  setError: SetErrorFn
): MRT_ColumnDef<ExpressOnboardingGenerator> {
  return {
    id: "tr_id",
    accessorKey: "tr_id",
    header: "TR-ID",
    enableGlobalFilter: false,
    Cell: ({ cell, row }) => {
      const value = cell.getValue<ExpressOnboardingGenerator["tr_id"]>();

      const { expressOnboardingGeneratorSave, pendingGeneratorIds } =
        useExpressOnboardingMutations();

      const { showErrorToast } = useToast();

      const rowIsSaving = pendingGeneratorIds.has(row.original.id);

      const cantEditReason = row.original.uneditable_fields?.tr_id;

      return (
        <Tooltip disabled={!cantEditReason} label={cantEditReason}>
          <TextInput
            defaultValue={value ?? undefined}
            disabled={!!cantEditReason || rowIsSaving}
            error={getError(row.original.id, "tr_id")}
            loading={rowIsSaving}
            placeholder={GENERATOR_FORM_DATA["einheit.tr_id"].placeholder}
            onBlur={(event) => {
              const currentValue =
                event.target.value.trim() === ""
                  ? null
                  : event.target.value.trim();

              const hasChange = currentValue !== value?.trim();
              if (!hasChange) {
                return;
              }

              const validation =
                !currentValue || isValidTrId(currentValue ?? "");

              setError(row.original.id, {
                tr_id: typeof validation === "string" ? validation : undefined
              });

              if (typeof validation === "string") {
                return;
              }

              expressOnboardingGeneratorSave
                .mutateAsync({
                  id: row.original.id,
                  tr_id: currentValue
                })
                .catch((error) => {
                  setErrorsFromResponseData(
                    showErrorToast,
                    error,
                    row.original,
                    (field: string, err: { message?: string }) =>
                      setError(row.original.id, {
                        [field]: err?.message ?? t("errors.UnknownError")
                      }),
                    t("errors.UnknownError")
                  );
                });
            }}
          />
        </Tooltip>
      );
    }
  };
}

export { getExpressOnboardingTableTrIdColumn };
